import {
   AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit
} from '@angular/core';
import {
   ColDef, GetContextMenuItemsParams, GridOptions, GridReadyEvent, RowClickedEvent, RowNode, RowSelectedEvent
} from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as Enumerable from 'linq';
import {
   centeredColumnDef, defaultLoadingOverlayTemplate, defaultMoneyCellDefinition, defaultTimestampFormatNoMs
} from 'projects/shared-components/ag-grid-contrib';
import { ShellClientService } from 'projects/shared-components/shell-communication/shell-client.service';
import { MessageBusService } from 'projects/shared-components/message-bus.service';
import { TimestampsService } from 'projects/shared-components/timestamps.service';
import { SessionService } from 'projects/shared-components/authentication/session-service.service';
import { TradingInstrumentDisplayNameService } from 'projects/shared-components/trading-instruments/trading-instrument-display-name.service';
import { Logger } from 'projects/shared-components/logging/logger.interface';
import { LoggerService } from 'projects/shared-components/logging/logger-factory.service';
import { DetectMethodChanges, makeCollarsOptionDisplayName, makeCollarsLedgerTimestamp } from 'projects/shared-components/utils';
import { parseOptionTicker } from 'projects/shared-components/options-common/options.model';
import {
   COLLARS_TXN_LABELS, CollarsLedgerDto, CollarsSessionDto, CollarsHistoryItemDto,
   GetCollarsSessionHistoryState, CollarsSessionHistoryStateDto, ResetCollarsSession
} from 'projects/shared-components/shell-communication/collars-operations';
import { CollarsSessionHistoryGroupRowRenderer } from './collars-session-history-group-row-renderer.component';
import { CollarsSessionHistoryScope } from './collars-session-history-scope';
import { CollarsSessionHistoryStateService } from './collars-session-history-state.service';

const KIND_ASSET = 0;

// CollarsBucketDto.level values (mirrors collars.component.ts).
const LEVEL_PORTFOLIO = 0;
const LEVEL_COMBO = 1;
const LEVEL_GROUP = 2;

const TXN_INTEREST = 5;   // CollarsTransactionType.Interest (mirrors collars.component.ts)

// Roll-up rows for the middle grids (built client-side from the flat snapshot list).
interface PortfolioRow { portfolioId: string; portfolioName: string; sessionPnL: number; accumulatedPnL: number; }
interface ComboRow { comboId: string; comboName: string; portfolioId: string; sessionPnL: number; accumulatedPnL: number; }
interface GroupRow { groupId: string; groupName: string; comboId: string; portfolioId: string; sessionPnL: number; accumulatedPnL: number; }

/** Existing Portfolio Hedging history surface, backed by a per-terminal snapshot plus event book.
 * Completed sessions use frozen snapshots; active totals are recalculated from position events. */
@Component({
   selector: 'ets-collars-session-history',
   templateUrl: 'collars-session-history.component.html',
   styleUrls: ['collars-session-history.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsSessionHistoryComponent implements OnInit, OnDestroy, AfterViewInit {

   constructor(
      private readonly _shellClient: ShellClientService,
      private readonly _toastr: ToastrService,
      private readonly _messageBus: MessageBusService,
      private readonly _timestamps: TimestampsService,
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _sessionService: SessionService,
      private readonly _displayNames: TradingInstrumentDisplayNameService,
      loggerService: LoggerService
   ) {
      this._logger = loggerService.createLogger('CollarsSessionHistoryComponent');
   }

   private readonly _logger: Logger;

   private _sessionsGrid: GridReadyEvent;
   private _portfoliosGrid: GridReadyEvent;
   private _combosGrid: GridReadyEvent;
   private _groupsGrid: GridReadyEvent;
   private _itemsGrid: GridReadyEvent;
   private _ledgerGrid: GridReadyEvent;

   private _sessions: CollarsSessionDto[] = [];
   private _snapshots: CollarsHistoryItemDto[] = [];
   private _selectedSession: CollarsSessionDto;
   // Bucket scope (owner request 2026-06-11): null = the unscoped general browser. When set, the
   // sessions list narrows to the bucket's terminal and the snapshot list to the bucket's subtree —
   // the middle grids roll up from that filtered list, so they show exactly the scoped ancestry chain.
   private _scope: CollarsSessionHistoryScope = null;
   private _terminalNames: { [id: string]: string } = {};
   private _userNames: { [id: string]: string } = {};

   private _unsubscriber = new Subject<any>();

   sessionsGridOptions: GridOptions;
   portfoliosGridOptions: GridOptions;
   combosGridOptions: GridOptions;
   groupsGridOptions: GridOptions;
   itemsGridOptions: GridOptions;
   ledgerGridOptions: GridOptions;

   historyListSectionSize: number;
   sessionBucketsListSectionSize: number;
   sessionDetailsSectionSize: number;
   bucketSectionSize: number;
   itemsSectionSize: number;
   ledgerSectionSize: number;

   // Zero-session-P&L filter (owner request 2026-06-12): the buckets and items grids hide rows whose
   // SESSION P&L is zero by default (snapshots capture every active item, including accumulated-only
   // ones). Right-click "Show Zero Session P&L Rows" flips it — dialog lifetime, never persisted.
   private _showLiveZeros = true;
   private _showClosedZeros = false;
   get showZeroSessionPnL(): boolean { return this._selectedSession && this._selectedSession.isActive ? this._showLiveZeros : this._showClosedZeros; }
   private readonly _state = new CollarsSessionHistoryStateService();
   private _open = false;
   private _viewEpoch = 0;
   private _detailEpoch = 0;
   private _rendering = false;
   private _reads: {[key: string]: Promise<void>} = {};
   private _pending: {[key: string]: boolean} = {};
   private _buffers: {[terminal: string]: {[key: string]: {kind: string; value: any}}} = {};
   private _selectedPortfolioId: string = null;
   private _selectedComboId: string = null;
   private _selectedGroupId: string = null;
   private _selectedItem: CollarsHistoryItemDto = null;
   stale = false;
   private _connected = true;
   private _sizedGrids = new Set<any>();
   private _sizeTimers = new Set<any>();
   private _staleTerminals: {[key: string]: boolean} = {};

   // Last UNFILTERED drill-down sets, kept so the zero-filter toggle can re-render in place.
   private _portfolioRows: PortfolioRow[] = [];
   private _comboRows: ComboRow[] = [];
   private _groupRows: GroupRow[] = [];
   private _itemRows: CollarsHistoryItemDto[] = [];

   get isSuperUser(): boolean {
      return this._sessionService.isSuperUser;
   }

   ngOnInit(): void {
      this.sessionsGridOptions = this.makeSessionsGrid();
      this.portfoliosGridOptions = this.makeBucketGrid('portfolio');
      this.combosGridOptions = this.makeBucketGrid('combo');
      this.groupsGridOptions = this.makeBucketGrid('group');
      this.itemsGridOptions = this.makeItemsGrid();
      this.ledgerGridOptions = this.makeLedgerGrid();
   }

   ngAfterViewInit(): void {
      // OnPush + detach: grids update via their api (not CD); view-bound state changes go through
      // @DetectMethodChanges (mirrors the ETS session-history component).
      this._changeDetector.detach();
   }

   ngOnDestroy(): void {
      this._logger.debug('~');
      this._open = false;
      this.cancelSizing();
      this._viewEpoch++;
      this._state.reset();
      this._buffers = {};
      this._reads = {};
      this._pending = {};
      // Destroyed-while-open (host-menu teardown, e.g. logout) never fires onHidden — complete the
      // bus subscriptions here. Subscriptions only, no resetState(): the grids are being disposed
      // with this view and must not be touched.
      if (this._unsubscriber) {
         this._unsubscriber.next();
         this._unsubscriber.complete();
         this._unsubscriber = null;
      }
   }

   // ---- lifecycle (driven by the popup wrapper) ----

   @DetectMethodChanges({ isAsync: true })
   async onShown(scope?: CollarsSessionHistoryScope): Promise<void> {
      this.resetState();
      this._scope = scope || null;
      this._state.reset(this._scope ? this._scope.bucketId : null);
      this._open = true;
      this.setDefaultSectionSizes();
      this.subscribeToMessages();
      await this.reloadHistory();
   }

   @DetectMethodChanges()
   onHidden(): void {
      this.resetState();
   }

   // ---- grid-ready ----

   onSessionsGridReady(args: GridReadyEvent): void { this._sessionsGrid = args; args.api.sizeColumnsToFit(); this.updateLoading(); }
   onPortfoliosGridReady(args: GridReadyEvent): void { this._portfoliosGrid = args; args.api.sizeColumnsToFit(); this.updateLoading(); }
   onCombosGridReady(args: GridReadyEvent): void { this._combosGrid = args; args.api.sizeColumnsToFit(); this.updateLoading(); }
   onGroupsGridReady(args: GridReadyEvent): void { this._groupsGrid = args; args.api.sizeColumnsToFit(); this.updateLoading(); }
   onItemsGridReady(args: GridReadyEvent): void { this._itemsGrid = args; args.api.sizeColumnsToFit(); this.updateLoading(); }
   onLedgerGridReady(args: GridReadyEvent): void { this._ledgerGrid = args; args.api.sizeColumnsToFit(); this.updateLoading(); }

   // ---- selection / drill-down ----

   @DetectMethodChanges({ isAsync: true })
   async onSessionRowSelected(session: CollarsSessionDto): Promise<void> {
      if (!session || this._rendering) { return; }
      this._selectedSession = session;
      this._detailEpoch++;
      this._selectedPortfolioId = this._selectedComboId = this._selectedGroupId = null;
      this._selectedItem = null;
      this.renderDetails();
      await this.reloadHistory(session.terminalId);
   }

   @DetectMethodChanges()
   onPortfolioRowSelected(row: PortfolioRow): void {
      this._selectedPortfolioId = row.portfolioId;
      this._selectedComboId = this._selectedGroupId = null;
      this._selectedItem = null;
      this.renderDetails();
   }
   @DetectMethodChanges()
   onComboRowSelected(row: ComboRow): void {
      this._selectedPortfolioId = row.portfolioId;
      this._selectedComboId = row.comboId;
      this._selectedGroupId = null;
      this._selectedItem = null;
      this.renderDetails();
   }
   @DetectMethodChanges()
   onGroupRowSelected(row: GroupRow): void {
      this._selectedPortfolioId = row.portfolioId;
      this._selectedComboId = row.comboId;
      this._selectedGroupId = row.groupId;
      this._selectedItem = null;
      this.renderDetails();
   }

   private hideZeros<T extends { sessionPnL: number }>(rows: T[]): T[] {
      return this.showZeroSessionPnL ? rows : rows.filter((r: any) => Math.abs(r.sessionPnL) > 1e-9
         || r.positionId && this._selectedItem && r.positionId === this._selectedItem.positionId
         || r.historyLevel === 'groupId' && r.groupId === this._selectedGroupId
         || r.historyLevel === 'comboId' && r.comboId === this._selectedComboId
         || r.historyLevel === 'portfolioId' && r.portfolioId === this._selectedPortfolioId);
   }
   @DetectMethodChanges()
   toggleZeroSessionPnLRows(): void {
      if (this._selectedSession && this._selectedSession.isActive) { this._showLiveZeros = !this._showLiveZeros; }
      else { this._showClosedZeros = !this._showClosedZeros; }
      this.renderDetails();
   }
   @DetectMethodChanges()
   onItemRowSelected(item: CollarsHistoryItemDto): void {
      if (!item || this._rendering) { return; }
      this._selectedItem = item;
      this.renderLedger();
   }

   @DetectMethodChanges({ isAsync: true })
   async resetSession(session: CollarsSessionDto): Promise<void> {
      if (!session || !this.isSuperUser) { return; }

      this._sessionsGrid.api.showLoadingOverlay();
      try {
         await this._shellClient.processCommand(new ResetCollarsSession(session.terminalId));
         // The CollarsSessionEndedDto push reloads the session list; no optimistic mutation here.
      } catch (e) {
         this._toastr.error('"Reset Session" completed with errors');
         this._logger.error('resetSession()', { error: e.stack || e, terminal: session.terminalId });
      } finally {
         if (this._open) { this.updateLoading(); }
      }
   }

   @DetectMethodChanges()
   toggleSectionFullWidth(section?: 'sessions' | 'buckets' | 'details'): void {
      const anyFull = this.historyListSectionSize === 100
         || this.sessionBucketsListSectionSize === 100
         || this.sessionDetailsSectionSize === 100;

      if (anyFull) {
         this.setDefaultSectionSizes();
         return;
      }

      this.historyListSectionSize = section === 'sessions' ? 100 : 0;
      this.sessionBucketsListSectionSize = section === 'buckets' ? 100 : 0;
      this.sessionDetailsSectionSize = section === 'details' ? 100 : 0;
   }

   // ---- loaders ----

   private reloadHistory(terminalId?: string): Promise<void> {
      if (!this._open) { return Promise.resolve(); }
      const key = terminalId || '*';
      if (terminalId && this._reads['*']) { this._pending['*'] = true; return this._reads['*']; }
      if (this._reads[key]) { this._pending[key] = true; return this._reads[key]; }
      const reads = this._reads, pending = this._pending, epoch = this._viewEpoch;
      // Deferring one microtask ensures the read is registered before events can arrive.
      reads[key] = Promise.resolve().then(async () => {
         do {
            pending[key] = false;
            const detailEpoch = this._detailEpoch;
            const selected = this._selectedSession;
            try {
               const reply = await this._shellClient.processQuery<CollarsSessionHistoryStateDto>(
                  new GetCollarsSessionHistoryState(terminalId ? [terminalId] : undefined,
                     this._scope ? this._scope.bucketId : undefined, selected ? selected.sessionId : undefined));
               if (!this._open || this._viewEpoch !== epoch) { return; }
               const returned = (reply && reply.terminals) || [];
               if (!terminalId) {
                  this._state.terminalIds().filter(id => !returned.some(t => t.terminalId === id)).forEach(id => this._state.remove(id));
               } else if (!returned.length) { this._state.remove(terminalId); }
               returned.forEach(book => {
                  if (!this._state.replace(book)) { pending[key] = true; return; }
                  const buffered = this._buffers[book.terminalId];
                  delete this._buffers[book.terminalId];
                  Object.keys(buffered || {}).map(k => buffered[k]).sort((a, b) => a.value.eventRevision - b.value.eventRevision)
                     .forEach(e => {
                        if (this.applyHistoryEvent(e.kind, e.value, false) === 'reload') { pending[key] = true; }
                     });
               });
               if (!terminalId) {
                  Object.keys(this._buffers).filter(id => !this._state.book(id)).forEach(id => delete this._buffers[id]);
               }
               if (detailEpoch !== this._detailEpoch) { pending[key] = true; }
               if (!terminalId) { this._staleTerminals = {}; } else { delete this._staleTerminals[terminalId]; }
               this.updateStale();
               this.renderState(detailEpoch === this._detailEpoch);
            } catch (e) {
               if (!this._open || this._viewEpoch !== epoch) { return; }
               if (!this.stale) { this._toastr.error('Session History could not refresh; displayed values may be stale.'); }
               this._staleTerminals[key] = true;
               this.updateStale();
               this._logger.error('history state read', {error: e.stack || e, terminalId});
               this._changeDetector.detectChanges();
               // No retry loop. A later reconnect, reopen or lifecycle event requests a new read.
               pending[key] = false;
            }
         } while (pending[key] && this._open && this._viewEpoch === epoch);
      }).finally(() => {
         delete reads[key]; delete pending[key];
         if (this._open && this._viewEpoch === epoch) { this.updateLoading(); }
      });
      this.updateLoading();
      return reads[key];
   }

   @DetectMethodChanges()
   private renderState(details = true): void {
      if (!this._open) { return; }
      this._sessions = this._state.sessions().sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
      this._sessions.forEach(s => { this._terminalNames[s.terminalId] = s.terminalName; this._userNames[s.terminalId] = s.userName; });
      if (this._selectedSession) {
         const old = this._selectedSession;
         this._selectedSession = this._sessions.find(s => s.sessionId === old.sessionId)
            || (old.isActive ? this._sessions.find(s => s.terminalId === old.terminalId && s.isActive) : null);
      }
      this._rendering = true;
      try {
         const first = this._sessionsGrid && this._sessionsGrid.api.getDisplayedRowCount() === 0;
         if (this._sessionsGrid) {
            this._sessionsGrid.api.setRowData(this._sessions);
            if (this._selectedSession) { const node = this._sessionsGrid.api.getRowNode(this._selectedSession.sessionId); if (node) { node.setSelected(true); } }
            if (first) { this.expandTerminalGroups(); }
         }
         if (details) { this.renderDetails(); }
      } finally { this._rendering = false; this.updateLoading(); }
   }

   private renderDetails(): void {
      const wasRendering = this._rendering;
      this._rendering = true;
      try {
         this._snapshots = this._selectedSession ? this._state.items(this._selectedSession) : [];
         const roll = (items: CollarsHistoryItemDto[], field: string, name: string): any[] => Enumerable.from(items)
            .groupBy(x => x[field]).select(gr => Object.assign({}, gr.first(), {
               [field]: gr.key(), [name]: gr.first()[name], sessionPnL: gr.sum(x => x.sessionPnL),
               accumulatedPnL: gr.sum(x => x.accumulatedPnL)
            })).toArray().map(row => { delete row.positionId; row.historyLevel = field; return row; });
         this._portfolioRows = this.alphabetical(roll(this._snapshots, 'portfolioId', 'portfolioName'), 'portfolioName', 'portfolioId');
         if (this._selectedPortfolioId && !this._portfolioRows.some(r => r.portfolioId === this._selectedPortfolioId)) {
            this._selectedPortfolioId = this._selectedComboId = this._selectedGroupId = null;
         }
         const portfolioItems = this._snapshots.filter(i => i.portfolioId === this._selectedPortfolioId);
         this._comboRows = this._selectedPortfolioId ? this.alphabetical(roll(portfolioItems, 'comboId', 'comboName'), 'comboName', 'comboId') : [];
         if (this._selectedComboId && !this._comboRows.some(r => r.comboId === this._selectedComboId)) { this._selectedComboId = this._selectedGroupId = null; }
         const comboItems = portfolioItems.filter(i => i.comboId === this._selectedComboId);
         this._groupRows = this._selectedComboId ? this.alphabetical(roll(comboItems, 'groupId', 'groupName'), 'groupName', 'groupId') : [];
         if (this._selectedGroupId && !this._groupRows.some(r => r.groupId === this._selectedGroupId)) { this._selectedGroupId = null; }
         this._itemRows = this._selectedGroupId ? comboItems.filter(i => i.groupId === this._selectedGroupId)
            : this._selectedComboId ? comboItems : this._selectedPortfolioId ? portfolioItems : [];
         this._itemRows = this._itemRows.slice().sort((a, b) => this.compareNames(this.dispName(a.tradingInstrumentCode),
            this.dispName(b.tradingInstrumentCode)) || this.compareNames(a.positionId, b.positionId));
         if (this._selectedItem) {
            const old = this._selectedItem;
            const matches = this._itemRows.filter(i => i.groupId === old.groupId && i.tradingInstrumentCode === old.tradingInstrumentCode);
            this._selectedItem = this._itemRows.find(i => i.positionId === old.positionId) || (matches.length === 1 ? matches[0] : null);
         }
         this.setRows(this._portfoliosGrid, this._portfolioRows, this._selectedPortfolioId);
         this.setRows(this._combosGrid, this._comboRows, this._selectedComboId);
         this.setRows(this._groupsGrid, this._groupRows, this._selectedGroupId);
         this.setRows(this._itemsGrid, this._itemRows, this._selectedItem && this._selectedItem.positionId);
         this.renderLedger();
      } finally { this._rendering = wasRendering; }
   }
   private setRows(grid: GridReadyEvent, rows: any[], selectedId: string): void {
      if (!grid) { return; }
      grid.api.setRowData(this.hideZeros(rows));
      if (selectedId) { const node = grid.api.getRowNode(selectedId); if (node) { node.setSelected(true); } }
   }
   private renderLedger(): void {
      if (!this._ledgerGrid) { return; }
      const bucket = this._selectedGroupId || this._selectedComboId || this._selectedPortfolioId;
      const rows = this._selectedSession && (bucket || this._selectedItem)
         ? this._state.selectedLedger(this._selectedSession, bucket, this._selectedItem && this._selectedItem.positionId) : [];
      this._ledgerGrid.api.setRowData(rows);
   }

   private historyEvent(kind: string, value: any): void {
      if (!this._open || !value) { return; }
      if (!this._state.book(value.terminalId) && !this._reads['*']) { return; }
      if (this._reads['*'] || this._reads[value.terminalId]) {
         const buffer = this._buffers[value.terminalId] || (this._buffers[value.terminalId] = {});
         const key = kind + ':' + (value.positionId && kind === 'position' ? value.positionId : value.recordId || 'boundary');
         if (!buffer[key] || buffer[key].value.eventRevision < value.eventRevision) { buffer[key] = {kind, value}; }
         return;
      }
      const update = this.applyHistoryEvent(kind, value, true);
      if (update === 'changed') { this.renderState(!!this._selectedSession && this._selectedSession.terminalId === value.terminalId && this._selectedSession.isActive); }
   }
   private applyHistoryEvent(kind: string, value: any, request: boolean): string {
      const book = this._state.book(value.terminalId);
      if (!book) { return 'ignored'; }
      let result: string;
      if (kind === 'position') { result = this._state.position(value); }
      else if (kind === 'ledger') { result = this._state.ledger(value); }
      else { result = book.streamGeneration === value.streamGeneration && value.eventRevision <= book.capturedRevision ? 'ignored' : 'reload'; }
      if (result === 'reload' && request) { this.reloadHistory(value.terminalId); }
      return result;
   }
   private updateStale(): void {
      this.stale = !this._connected || Object.keys(this._staleTerminals).length > 0;
      this._changeDetector.detectChanges();
   }
   private subscribeToMessages(): void {
      this._unsubscriber = new Subject<any>();
      ['CollarsPositionChangedDto', 'CollarsLedgerChangedDto', 'CollarsSessionEndedDto', 'CollarsBookRecomputedDto'].forEach((type, i) => {
         this._messageBus.of(type).pipe(takeUntil(this._unsubscriber))
            .subscribe((m: any) => this.historyEvent(i === 0 ? 'position' : i === 1 ? 'ledger' : 'boundary', m.payload));
      });
      this._messageBus.of('EventStreamConnectionChangedUIMessage').pipe(takeUntil(this._unsubscriber)).subscribe((m: any) => {
         this._connected = m.payload.isConnected;
         this.updateStale();
         if (this._connected) { this.reloadHistory(); }
      });
      this._messageBus.of('DefaultTimezoneChanged').pipe(takeUntil(this._unsubscriber)).subscribe(() => {
         if (this._ledgerGrid) { this._ledgerGrid.api.refreshCells({force: true}); }
         if (this._sessionsGrid) { this._sessionsGrid.api.refreshCells({force: true}); }
      });
   }

   // ---- grid builders ----

   private makeSessionsGrid(): GridOptions {
      const self = this;
      return {
         rowData: [],
         defaultColDef: centeredColumnDef,
         columnDefs: [
            { headerName: 'Terminal', field: 'terminalName', enableRowGroup: true, rowGroup: true, hide: true },
            {
               headerName: 'Started', field: 'startDate', hide: true,
               valueFormatter: (p: any) => p.value
                  ? self._timestamps.getZonedFormattedDateTime(p.data.startDate, defaultTimestampFormatNoMs)
                  : ''
            },
            {
               headerName: 'Ended', field: 'endDate', sort: 'desc',
               valueFormatter: (p: any) => !p.value
                  ? '< Active >'
                  : self._timestamps.getZonedFormattedDateTime(p.data.endDate, defaultTimestampFormatNoMs)
            },
            self.moneyCol('Session P&L', 'sessionPnL'),
            self.moneyCol('Acc. P&L', 'accumulatedPnL')
         ],
         overlayLoadingTemplate: defaultLoadingOverlayTemplate,
         rowClass: 'ets-text-centered',
         rowSelection: 'single',
         rowModelType: 'clientSide',
         immutableData: true,
         rowGroupPanelShow: 'never',
         groupUseEntireRow: true,
         suppressDragLeaveHidesColumns: true,
         suppressAggFuncInHeader: true,
         suppressCellSelection: true,
         frameworkComponents: { collarsSessionGroupRowRenderer: CollarsSessionHistoryGroupRowRenderer },
         groupRowRendererParams: { innerRenderer: 'collarsSessionGroupRowRenderer', suppressCount: true },
         onGridReady: (args: GridReadyEvent) => self.onSessionsGridReady(args),
         // Keep the session grid fitted to its available width.
         onFirstDataRendered: (args: any) => args.api.sizeColumnsToFit(),
         // Pin the active (not-yet-reset) session(s) to the top, like ETS.
         postSort: (rowNodes: RowNode[]) => {
            let nextInsertPos = 0;
            for (let i = 0; i < rowNodes.length; i++) {
               const data: CollarsSessionDto = rowNodes[i].data;
               if (data && !data.endDate) {
                  rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
                  nextInsertPos++;
               }
            }
         },
         onRowSelected: (args: RowSelectedEvent) => {
            if (self._rendering || !args.node || args.node.group || !args.node.isSelected()) { return; }
            self.onSessionRowSelected(args.node.data);
         },
         getRowNodeId: (data: CollarsSessionDto) => data.sessionId,
         getContextMenuItems: (params: GetContextMenuItemsParams) => {
            const menu: any[] = [];
            // Reset is SUPER-USER ONLY (server-enforced too); offered only on the active session row. No delete.
            if (self.isSuperUser) {
               menu.push({
                  name: 'Reset',
                  disabled: !params.node || !params.node.data || !!params.node.data.endDate,
                  action: () => self.resetSession(params.node.data)
               });
               menu.push('separator');
            }
            // The zero-P&L toggle is GLOBAL across the dialog (owner, 2026-06-12) — offered on every
            // subsection's menu, one shared flag.
            menu.push({
               name: self.showZeroSessionPnL ? 'Hide Zero Session P&L Rows' : 'Show Zero Session P&L Rows',
               action: () => self.toggleZeroSessionPnLRows()
            });
            menu.push('separator');
            menu.push('autoSizeAll');
            menu.push({ name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() });
            menu.push('copy', 'export');
            return menu;
         }
      } as GridOptions;
   }

   private makeBucketGrid(level: 'portfolio' | 'combo' | 'group'): GridOptions {
      const self = this;
      const nameHeader = level === 'portfolio' ? 'Portfolio' : (level === 'combo' ? 'Combo' : 'Group');
      const nameField = level === 'portfolio' ? 'portfolioName' : (level === 'combo' ? 'comboName' : 'groupName');
      const idField = level === 'portfolio' ? 'portfolioId' : (level === 'combo' ? 'comboId' : 'groupId');

      return {
         rowData: [],
         defaultColDef: Object.assign({}, centeredColumnDef, {sortable: false}),
         columnDefs: [
            { headerName: nameHeader, field: nameField, sortable: true, sort: 'asc', comparator: (a, b) => self.compareNames(a, b) },
            Object.assign({ headerName: 'S. P&L', field: 'sessionPnL' }, defaultMoneyCellDefinition),
            Object.assign({ headerName: 'Acc. P&L', field: 'accumulatedPnL' }, defaultMoneyCellDefinition)
         ],
         overlayLoadingTemplate: defaultLoadingOverlayTemplate,
         rowClass: 'ets-text-centered',
         rowSelection: 'single',
         rowModelType: 'clientSide',
         immutableData: true,
         suppressCellSelection: true,
         getRowNodeId: (data: any) => data[idField],
         onGridReady: (args: GridReadyEvent) => {
            if (level === 'portfolio') { self.onPortfoliosGridReady(args); }
            else if (level === 'combo') { self.onCombosGridReady(args); }
            else { self.onGroupsGridReady(args); }
         },
         // Keep bucket columns fitted to their available width.
         onFirstDataRendered: (args: any) => args.api.sizeColumnsToFit(),
         onRowClicked: (args: RowClickedEvent) => {
            if (!args.node || args.node.group || !args.node.data) { return; }
            if (level === 'portfolio') { self.onPortfolioRowSelected(args.node.data); }
            else if (level === 'combo') { self.onComboRowSelected(args.node.data); }
            else { self.onGroupRowSelected(args.node.data); }
         },
         getContextMenuItems: (params: any) => [
            {
               name: self.showZeroSessionPnL ? 'Hide Zero Session P&L Rows' : 'Show Zero Session P&L Rows',
               action: () => self.toggleZeroSessionPnLRows()
            },
            'separator', 'autoSizeAll',
            { name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() },
            'copy', 'export'
         ]
      } as GridOptions;
   }

   private makeItemsGrid(): GridOptions {
      const self = this;
      return {
         rowData: [],
         defaultColDef: Object.assign({}, centeredColumnDef, { cellStyle: { 'text-align': 'center' }, sortable: false }),
         columnDefs: [
            { headerName: 'Symbol', field: 'tradingInstrumentCode', flex: 1, minWidth: 170, sortable: true, sort: 'asc', comparator: (a, b, na, nb) => self.compareNames(self.dispName(a), self.dispName(b))
               || self.compareNames(na.data.positionId, nb.data.positionId), valueFormatter: (p: any) => self.dispName(p.value) },
            { headerName: 'Type', colId: 'type', maxWidth: 90, valueGetter: (p: any) => self.instrumentType(p.data) },
            { headerName: 'Net', field: 'netPosition', maxWidth: 90 },
            self.moneyCol('Session P&L', 'sessionPnL'),
            self.moneyCol('Acc. P&L', 'accumulatedPnL'),
            // Bucket metadata: Combo, Group, Portfolio (owner re-order 2026-06-12).
            { headerName: 'Combo', field: 'comboName', minWidth: 120 },
            { headerName: 'Group', field: 'groupName', minWidth: 120 },
            { headerName: 'Portfolio', field: 'portfolioName', minWidth: 120 }
         ],
         overlayLoadingTemplate: defaultLoadingOverlayTemplate,
         rowClass: 'ets-text-centered',
         rowSelection: 'single',
         rowModelType: 'clientSide',
         immutableData: true,
         suppressColumnVirtualisation: true,
         suppressCellSelection: true,
         getRowNodeId: (data: CollarsHistoryItemDto) => data.positionId,
         onGridReady: (args: GridReadyEvent) => self.onItemsGridReady(args),
         // Auto-size to content with a small delay (owner, 2026-06-12): at first render the split
         // area's width is not settled yet, so an immediate measure under-sizes some columns.
         onFirstDataRendered: (args: any) => self.autoSizeFirstRows(args),
         onRowDataUpdated: (args: any) => self.autoSizeFirstRows(args),
         onRowSelected: (args: RowSelectedEvent) => {
            if (self._rendering || !args.node || args.node.group || !args.node.data || !args.node.isSelected()) { return; }
            self.onItemRowSelected(args.node.data);
         },
         // ETS session-history parity (owner request 2026-06-12): blow the right details column
         // (items + ledger together) to full width; the same item toggles back to the default split.
         getContextMenuItems: (params: any) => [
            { name: 'Toggle Full Size', action: () => self.toggleSectionFullWidth('details') },
            {
               name: self.showZeroSessionPnL ? 'Hide Zero Session P&L Rows' : 'Show Zero Session P&L Rows',
               action: () => self.toggleZeroSessionPnLRows()
            },
            'separator', 'autoSizeAll',
            { name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() },
            'copy', 'export'
         ]
      } as GridOptions;
   }

   // FLAT, non-drillable ledger (replaces the ETS Trades/Snapshots/Messages tabs).
   private makeLedgerGrid(): GridOptions {
      const self = this;
      return {
         rowData: [],
         defaultColDef: Object.assign({}, centeredColumnDef, { cellStyle: { 'text-align': 'center' }, sortable: false }),
         columnDefs: [
            { headerName: 'Symbol', field: 'tradingInstrumentCode', flex: 1, minWidth: 170, valueFormatter: (p: any) => self.dispName(p.value) },
            { headerName: 'Trans. Date', field: 'transactionDate', minWidth: 215, sort: 'asc', comparator: (a, b, na, nb) =>
               new Date(a).getTime() - new Date(b).getTime() || na.data.sequence - nb.data.sequence, valueFormatter: (p: any) => makeCollarsLedgerTimestamp(p.value, self._timestamps.getDefaultTimezone('UTC')) },
            { headerName: 'Trans. Type', field: 'transactionType', maxWidth: 120, valueFormatter: (p: any) => self.txnLabel(p.value), tooltipValueGetter: (p: any) => (p.data && p.data.note) || '' },
            // Interest charge metadata, same treatment as the panel ledger (owner spec 2026-06-12):
            // Price = stamped rate ("10%"), Qty = charge days ("7d"), Net Position = running charged days.
            { headerName: 'Price', field: 'price', maxWidth: 90, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.pct(p.value) : self.num(p.value) },
            { headerName: 'Qty', field: 'quantity', maxWidth: 80, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.days(p.value) : ((p.value === null || p.value === undefined) ? '' : String(p.value)) },
            { headerName: 'Net Position', field: 'netPosition', minWidth: 120, valueFormatter: (p: any) => (p.data && p.data.transactionType === TXN_INTEREST) ? self.days(p.value) : ((p.value === null || p.value === undefined) ? '' : String(p.value)) },
            // Bucket metadata: Combo, Group, Portfolio, User (owner re-order 2026-06-12).
            { headerName: 'Combo', field: 'comboName', minWidth: 120 },
            { headerName: 'Group', field: 'groupName', minWidth: 120 },
            { headerName: 'Portfolio', field: 'portfolioName', minWidth: 120 },
            { headerName: 'User', colId: 'user', minWidth: 120, valueGetter: (p: any) => p.data ? self.userName(p.data.terminalId) : '', tooltipValueGetter: (p: any) => p.data ? self.terminalName(p.data.terminalId) : '' },
            self.moneyCol('P&L', 'itemPnL'),
            self.moneyCol('Portfolio P&L', 'portfolioPnL'),
            self.moneyCol('Combo P&L', 'comboPnL'),
            self.moneyCol('Group P&L', 'groupPnL')
         ],
         overlayLoadingTemplate: defaultLoadingOverlayTemplate,
         rowClass: 'ets-text-centered',
         rowSelection: 'single',
         rowModelType: 'clientSide',
         immutableData: true,
         suppressColumnVirtualisation: true,
         suppressCellSelection: true,
         getRowNodeId: (data: CollarsLedgerDto) => data.recordId,
         onGridReady: (args: GridReadyEvent) => self.onLedgerGridReady(args),
         // Same delayed content auto-size as the items grid above (shared details column).
         onFirstDataRendered: (args: any) => self.autoSizeFirstRows(args),
         onRowDataUpdated: (args: any) => self.autoSizeFirstRows(args),
         // Same toggles as the items grid above — the two grids share the details column, and the
         // zero-P&L flag is global across the dialog.
         getContextMenuItems: (params: any) => [
            { name: 'Toggle Full Size', action: () => self.toggleSectionFullWidth('details') },
            {
               name: self.showZeroSessionPnL ? 'Hide Zero Session P&L Rows' : 'Show Zero Session P&L Rows',
               action: () => self.toggleZeroSessionPnLRows()
            },
            'separator', 'autoSizeAll',
            { name: 'Size Columns To Fit', action: () => params.api.sizeColumnsToFit() },
            'copy', 'export'
         ]
      } as GridOptions;
   }

   // ---- helpers (mirror collars.component.ts) ----

   private compareNames(a: string, b: string): number {
      return (a || '').localeCompare(b || '', undefined, {sensitivity: 'base', numeric: true});
   }
   private alphabetical<T>(rows: T[], name: string, id: string): T[] {
      return rows.slice().sort((a, b) => this.compareNames(a[name], b[name]) || this.compareNames(a[id], b[id]));
   }
   private updateLoading(): void {
      const loading = this._open && Object.keys(this._reads).length > 0;
      [this._sessionsGrid, this._portfoliosGrid, this._combosGrid, this._groupsGrid, this._itemsGrid, this._ledgerGrid]
         .forEach(grid => {
            if (!grid) { return; }
            if (loading) { grid.api.showLoadingOverlay(); }
            else { grid.api.hideOverlay(); }
         });
   }
   private autoSizeFirstRows(args: any): void {
      if (!this._open || !args.api.getDisplayedRowCount() || this._sizedGrids.has(args.api)) { return; }
      this._sizedGrids.add(args.api);
      const epoch = this._viewEpoch;
      const timer = setTimeout(() => {
         this._sizeTimers.delete(timer);
         if (this._open && this._viewEpoch === epoch) { args.columnApi.autoSizeAllColumns(); }
      }, 100);
      this._sizeTimers.add(timer);
   }
   private cancelSizing(): void {
      this._sizeTimers.forEach(timer => clearTimeout(timer));
      this._sizeTimers.clear();
      this._sizedGrids.clear();
   }

   private moneyCol(header: string, field: string): ColDef {
      return Object.assign({ headerName: header, field, maxWidth: 130, aggFunc: 'sum' }, defaultMoneyCellDefinition) as ColDef;
   }

   private num(v: number): string {
      return (v === null || v === undefined) ? '' : (+v).toFixed(2);
   }

   // Interest charge metadata (mirrors collars.component.ts): rate fraction -> trimmed percent; day
   // count -> "Nd". Blank on 0/null — a zero is the legacy (pre-stamp) sentinel, never backfilled.
   private pct(v: number): string {
      return v ? `${parseFloat((+v * 100).toFixed(2))}%` : '';
   }

   private days(v: number): string {
      return v ? `${v}d` : '';
   }

   private dispName(code: string): string {
      if (!code) { return ''; }
      const t = parseOptionTicker(code);
      if (t) {
         const ulName = this._displayNames.getDisplayNameForTicker(t.underlying);
         return makeCollarsOptionDisplayName(t, ulName);   // historical: no live DTE suffix
      }
      return this._displayNames.getDisplayNameForTicker(code) || code;
   }

   private instrumentType(data: CollarsHistoryItemDto): string {
      if (!data) { return ''; }
      if (data.kind === KIND_ASSET) { return 'Asset'; }
      const t = parseOptionTicker(data.tradingInstrumentCode);
      return t ? t.type : 'Option';
   }

   private txnLabel(v: number): string {
      return (v >= 0 && v < COLLARS_TXN_LABELS.length) ? COLLARS_TXN_LABELS[v] : String(v);
   }

   private terminalName(id: string): string {
      return (id && this._terminalNames[id]) || '';
   }

   private userName(id: string): string {
      return (id && this._userNames[id]) || this.terminalName(id);
   }

   private expandTerminalGroups(): void {
      if (!this._sessionsGrid) { return; }
      this._sessionsGrid.api.forEachNode(node => {
         if (node.isExpandable()) { node.expanded = true; }
      });
      const epoch = this._viewEpoch;
      setTimeout(() => { if (this._open && epoch === this._viewEpoch) { this._sessionsGrid.api.onGroupExpandedOrCollapsed(); } }, 50);
   }

   @DetectMethodChanges()
   private setDefaultSectionSizes(): void {
      this.historyListSectionSize = 33;
      this.sessionBucketsListSectionSize = 33;
      this.sessionDetailsSectionSize = 34;
      this.bucketSectionSize = 33.3;
      this.itemsSectionSize = 50;
      this.ledgerSectionSize = 50;
   }

   private resetState(): void {
      if (this._unsubscriber) {
         this._unsubscriber.next();
         this._unsubscriber.complete();
         this._unsubscriber = null;
      }
      this._snapshots = [];
      this._sessions = [];
      this._selectedSession = null;
      this._scope = null;
      this._showLiveZeros = true;
      this._showClosedZeros = false;
      this._open = false;
      this.cancelSizing();
      this._viewEpoch++;
      this._detailEpoch++;
      this._state.reset();
      this._reads = {};
      this._pending = {};
      this._buffers = {};
      this._selectedPortfolioId = this._selectedComboId = this._selectedGroupId = null;
      this._selectedItem = null;
      this.stale = false;
      this._connected = true;
      this._staleTerminals = {};
      this._portfolioRows = [];
      this._comboRows = [];
      this._groupRows = [];
      this._itemRows = [];
      this._terminalNames = {};
      this._userNames = {};
      this.updateLoading();

      if (this._ledgerGrid) { this._ledgerGrid.api.setRowData([]); }
      if (this._itemsGrid) { this._itemsGrid.api.setRowData([]); }
      if (this._groupsGrid) { this._groupsGrid.api.setRowData([]); }
      if (this._combosGrid) { this._combosGrid.api.setRowData([]); }
      if (this._portfoliosGrid) { this._portfoliosGrid.api.setRowData([]); }
      if (this._sessionsGrid) { this._sessionsGrid.api.setRowData([]); }
   }
}
